import { Backdrop, CircularProgress, Container, Grid } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import Success from '../../../components/Form/Success';
import PageTitle from '../../../components/ui/PageTitleUI';
import { useAuth } from '../../../hooks/auth';
import { ConciergeSearch } from '../../../interfaces/Concierge';
import { MvmEntry } from "../../../interfaces/Mvm";
import MvmStatus from '../../../interfaces/MvmStatus';
import MvmApi from '../../../services/MvmApi';
import permissions from '../../../utils/Permissions';
import MvmFooter from '../components/MvmFooter';
import MvmSummary from '../components/MvmSummary';
import { useStyles } from '../Style';
import MvmCompositionPartInfo from '../components/MvmCompositionPartInfo';

interface IFormInput {
    mvmNumber: any;
    invoices: any;
    name: string;
    rg: string;
    cpf: string;
    cnh: string;
    ajudantes: string;
    concierge: any;
    conciergeCode: string;
    mvmNumberCart: string;
    vehiclePlateNumberCart: string;
}

const opcoes = [
    { url: '/CheckIn', descricao: 'Registrar Nova Entrada', state: {}},
]

const CheckInOut: React.FC = () => {
    const classes = useStyles()
    const { user, hasPermission } = useAuth()
    const history = useHistory()
    const { t: _t } = useTranslation("General")
    const { t: tval } = useTranslation("Validation");
    const { t: tMvm } = useTranslation("Mvm");
    const { t } = useTranslation("Checkin")
    const { enqueueSnackbar } = useSnackbar();
    const [circularLoading, setCircularLoading] = useState(false)
    const [loading, setLoading] = useState(true);
    const [concierges, setConcierges] = useState<any>([]);
    const [mvm, setMvm] = useState<any>(null);
    const [cartMvmEntrance, setCartMvmEntrance] = useState<any>(null)
    const { control, setValue, getValues, setError, clearErrors  } = useForm<IFormInput>(
        {
            defaultValues: { mvmNumber: {}, concierge: {}, conciergeCode: '' }
        })
    const [success, setSuccess] = useState(false);
    
    const onSummaryChange = (data: any) => {
        if (!data) {
            setMvm(null);
            setCartMvmEntrance(null)
            return
        }

        setMvm(data);
        setCompositionInfo(data.mvmType, data.compositions)
        setValue("invoices", data.invoices);
    }

    const setCompositionInfo = (mvmType : string, compositions : any[]) => {
        if (compositions.length === 0 || mvmType === "C") 
            return 

        var cartEntrance = compositions.filter((f : any) => f.movementType == "EN")[0]?.cartMvm

        if (cartEntrance)
            setCartMvmEntrance(cartEntrance)
    }

    useEffect(() => {
        if (!hasPermission(permissions.MVM.RegisterEntry))
            history.replace('NoPermission');
    }, [history, user])

    const getConcierges = useCallback(
        async (data: ConciergeSearch) => {               
            setLoading(true);

            MvmApi.searchConcierge(data)
                .then(result => {
                    if (result && result.items) {
                        setConcierges(result.items);}
                    else {
                        setConcierges([]);
                    }
                }).catch(() => {
                    setConcierges([]);
                }).finally(() => {
                    setLoading(false);
                });
        },
        [],
    );

    useEffect(() => {
        MvmApi.getUserConcierge(user.userName, +user.company.plantID).then(conci => {
            setValue("concierge", {code: conci.code, label: conci.code});
            setValue("conciergeCode", conci.code);

        });
    }, [concierges])


    useEffect(() => {

        let conciergeFilter:ConciergeSearch = {
            id:0,
            code: "",
            plantId: Number(user.company.plantID),
            deleted: false,
            pageSize: 999
        };

        getConcierges(conciergeFilter);
    },[]);

    const yupValidation = async (data: any) => {            

        clearErrors(["conciergeCode"])

        try {
            const schema = Yup.object().shape({
                conciergeCode: Yup.object()
                    .nullable()
                    //.required(tval("requiredField")),
                    .shape({code: Yup.string().required(tval("requiredField"))}),
            });

            await schema.validate(data, {
                abortEarly: false,
            })

        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError("concierge", { message: error.message })
                })
            }

            return false
        }

        return true
    }

    async function CheckIn()
    {
        if (mvm.mvmType === 'C') {
            enqueueSnackbar(tMvm("mvmCartCannotEnterExit"), { variant: 'warning' })
            return
        }

        setCircularLoading(true);

        let data = getValues();

        const mvmEntry: MvmEntry = {
            mvmId: mvm.id,
            conciergeCode: data.concierge.code,
        };

        if (!(await yupValidation(mvmEntry)))
        { 
            setCircularLoading(false); 
            return;
        }

        MvmApi.entryMvm(mvmEntry)
        .then(() => {
            enqueueSnackbar(_t("returnSubmitEditFormSuccess"), { variant: 'success' });
            setSuccess(true);
            })
        .finally(() => { setCircularLoading(false);});
    }

    if (success)
        return (
            <Container maxWidth={false} component="main">
                <div className={classes.rootContent}>
                    <div className={classes.paper}>
                        <Success opcoes={opcoes}/>
                    </div>
                </div>
            </Container>
        )
    else   
        return (
        <Container maxWidth={false} component="main">

            <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
           
            <PageTitle title={t("registered entrance") + (mvm ? " - " + mvm.mvmNumber:"")} />

            <form translate={undefined} >
            <div className={classes.rootContent}>
                <MvmSummary
                    control={control}
                    name={"summary"}
                    onChange={onSummaryChange}
                    setValue={setValue}
                    showAutoComplete={false}
                    showDriverData={true}
                    mvmStatus={[MvmStatus.FLAG_STATUS_PENDING_ENTRANCE]}
                    includeProperties={["Invoices", "Compositions"]}
                />
            </div>

            {mvm && mvm.mvmType !== "C" && (mvm.composition || mvm.compositions.length > 0) &&
                <div className={classes.rootContent}>
                    <MvmCompositionPartInfo 
                        mvm={cartMvmEntrance}
                        control={control}
                        setValue={setValue}
                        type={tMvm('cart')}
                        movementType={tMvm('entrance')} />
                </div>
            }
            
            {loading || mvm == null ? <></> :
            <>
                <MvmFooter
                    control={control}
                    name={"footer"}
                    onChange={onSummaryChange}
                    setValue={setValue}
                    checkInOrOut={CheckIn}
                    pngValidated={mvm.pngValidated}
                    conciergeReleaseDate={mvm.conciergeReleaseDate}
                    status={mvm.status}
                    mvmType={mvm.mvmType}
                    concierges={concierges}
                    mvmStatus={[MvmStatus.FLAG_STATUS_PENDING_ENTRANCE]} />
            </>}
           
            </form>
        </Container>
    )
}
export default CheckInOut;